import Vue from "vue";
import { getFastDFSPath } from "@/servers/api";

// 持久化缓存 FastDFS 前缀，刷新页面后仍可复用。
const FAST_PATH_CACHE_KEY = "FAST_PATH_CACHE";
// 请求按 Token 隔离，避免会话切换时复用旧请求。
let fastPathPromise = null;
let initializePromise = null;
let fastPathToken = null;
let initializedToken = null;

const app = {
  state: {
    baseUrl: process.env.VUE_APP_API_BASE_URL,
    picPrefix: "",
  },
  getters: {
    getApi: (state) => {
      return state.apiUrl;
    },
    getPicRoot: (state) => {
      return state.picPrefix;
    },
    /**
     * 全局图片路径拼接方法
     * @param {string} path FastDFS相对路径
     * @returns {string} 拼接后的完整URL
     */
    buildImageUrl: (state) => (path) => {
      if (!path) return "";
      if (/^https?:\/\//i.test(path)) return path;
      return (state.picPrefix || "") + path;
    },
  },
  mutations: {
    SET_PIC_ROOT: (state, data) => {
      state.picPrefix = data;
    },
    RESET_COMMON_DATA: (state) => {
      state.picPrefix = "";
    },
  },
  actions: {
    getFastPath({ commit }) {
      const token = Vue.ls.get("ACCESS_TOKEN");
      if (!token) {
        commit("RESET_COMMON_DATA");
        return Promise.reject(new Error("missing access token"));
      }
      const cached = Vue.ls.get(FAST_PATH_CACHE_KEY);
      if (cached && cached.token === token && cached.prefix) {
        fastPathToken = token;
        commit("SET_PIC_ROOT", cached.prefix);
        return Promise.resolve(cached.prefix);
      }
      return getFastDFSPath()
        .then((res) => {
          const value = res ? "http://" + res : "";
          if (Vue.ls.get("ACCESS_TOKEN") !== token) {
            return value;
          }
          fastPathToken = token;
          Vue.ls.set(FAST_PATH_CACHE_KEY, { token, prefix: value });
          commit("SET_PIC_ROOT", value);
          return value;
        })
        .catch((error) => {
          if (Vue.ls.get("ACCESS_TOKEN") === token) {
            Vue.ls.remove(FAST_PATH_CACHE_KEY);
            commit("SET_PIC_ROOT", "");
          }
          return Promise.reject(error);
        });
    },
    /**
     * 兼容旧页面的图片前缀入口，复用持久化缓存和同 Token 请求。
     * 公共初始化流程优先使用 initializeAfterLogin。
     */
    ensureFastPath({ state, dispatch }) {
      const token = Vue.ls.get("ACCESS_TOKEN");
      if (!token) {
        dispatch("resetAfterLogout");
        return Promise.reject(new Error("missing access token"));
      }
      if (fastPathToken !== token) {
        fastPathToken = token;
        state.picPrefix = "";
      }
      if (state.picPrefix) {
        return Promise.resolve(state.picPrefix);
      }
      if (fastPathPromise && fastPathPromise.token === token) {
        return fastPathPromise.promise;
      }
      const promise = dispatch("getFastPath").finally(() => {
        if (fastPathPromise && fastPathPromise.promise === promise) {
          fastPathPromise = null;
        }
      });
      fastPathPromise = { token, promise };
      return promise;
    },
    /**
     * 登录成功后的公共数据统一初始化入口。
     * 页面刷新时会先复用持久化缓存，并按 Token 去重初始化请求。
     */
    initializeAfterLogin({ dispatch }) {
      const token = Vue.ls.get("ACCESS_TOKEN");
      if (!token) {
        dispatch("resetAfterLogout");
        return Promise.reject(new Error("missing access token"));
      }
      if (initializedToken === token) {
        return Promise.resolve();
      }
      if (initializePromise && initializePromise.token === token) {
        return initializePromise.promise;
      }

      const promise = dispatch("ensureFastPath")
        .then(() => {
          initializedToken = token;
        });
      initializePromise = { token, promise };
      return promise.finally(() => {
        if (initializePromise && initializePromise.promise === promise) {
          initializePromise = null;
        }
      });
    },
    resetAfterLogout({ commit }) {
      initializedToken = null;
      fastPathToken = null;
      fastPathPromise = null;
      initializePromise = null;
      Vue.ls.remove(FAST_PATH_CACHE_KEY);
      commit("RESET_COMMON_DATA");
    },
  },
  namespaced: true,
};
export default app;
